import type { MenuOption } from './menu';
import { normalizeText } from './menuDrinks';

/**
 * Logique d'AFFICHAGE CLIENT du menu (fiche produit façon Uber).
 * 100 % pur → testable sans réseau ni DOM. Couvre : sous-options conditionnelles,
 * tarification « 1ʳᵉ offerte +X€ », total de ligne, validation d'ajout au panier.
 *
 * Les sélections sont indexées par SLUG de groupe :
 *   - choix unique  → string (le label choisi)
 *   - choix multiple → string[] (les labels cochés)
 */
export type Selections = Record<string, string | string[] | null | undefined>;

/** Normalise une sélection en tableau de labels. */
export function selectedLabels(sel: string | string[] | null | undefined): string[] {
  if (sel == null) return [];
  return Array.isArray(sel) ? sel : [sel];
}

/**
 * Un groupe est-il visible ? Vrai s'il n'a pas de dépendance, sinon si la valeur
 * déclencheuse (normalisée) est CONTENUE dans une valeur sélectionnée :
 *  - `dependsOn.optionSlug` défini → on ne regarde que ce groupe parent (sous-option classique) ;
 *  - `dependsOn.optionSlug` vide   → on regarde TOUTES les sélections du produit
 *    (cas « boisson au menu » : visible dès qu'un format contenant « boisson » est choisi).
 * Le « contient » (au lieu de l'égalité) gère « Boisson » ET « Boisson et frite ».
 */
export function isOptionVisible(option: MenuOption, selections: Selections): boolean {
  const dep = option.dependsOn;
  if (!dep) return true;
  const needle = normalizeText(dep.value);
  if (!needle) return true;
  const pool = dep.optionSlug
    ? selectedLabels(selections[dep.optionSlug])
    : Object.values(selections).flatMap(selectedLabels);
  return pool.some((label) => normalizeText(label).includes(needle));
}

/** Liste des groupes actuellement visibles selon les choix faits (sous-options incluses). */
export function visibleOptions(options: MenuOption[], selections: Selections): MenuOption[] {
  return options.filter((o) => isOptionVisible(o, selections));
}

/**
 * Supplément (en centimes) d'un groupe selon les valeurs choisies.
 * - mode 'flat' : somme des suppléments des valeurs choisies.
 * - mode 'first_free' : 1ʳᵉ valeur offerte, chaque valeur suivante + extraEachCents
 *   (en plus de l'éventuel supplément propre à la valeur).
 */
export function groupExtraCents(option: MenuOption, selections: Selections): number {
  const labels = selectedLabels(selections[option.slug]);
  let cents = 0;
  for (const label of labels) {
    const v = option.values.find((x) => x.label === label);
    if (v) cents += v.priceDeltaCents;
  }
  if (option.pricingMode === 'first_free' && labels.length > 1) {
    cents += (labels.length - 1) * option.extraEachCents;
  }
  return cents;
}

/** Total des suppléments de TOUS les groupes visibles (centimes). */
export function optionsTotalCents(options: MenuOption[], selections: Selections): number {
  return visibleOptions(options, selections).reduce(
    (sum, o) => sum + groupExtraCents(o, selections),
    0,
  );
}

/** Prix total d'une ligne de panier : prix de base + suppléments des options visibles. */
export function lineTotalCents(
  baseCents: number,
  options: MenuOption[],
  selections: Selections,
): number {
  return baseCents + optionsTotalCents(options, selections);
}

/** Résumé lisible des choix (groupes visibles), pour la ligne de panier / le ticket. */
export function selectionSummary(options: MenuOption[], selections: Selections): string {
  return visibleOptions(options, selections)
    .flatMap((o) => selectedLabels(selections[o.slug]))
    .join(', ');
}

/**
 * Options GROUPÉES lisibles pour le ticket cuisine (parité legacy : Viande: / Sauce: …).
 * Un groupe par option visible ayant au moins une valeur choisie. Pur.
 */
export function buildOptionLines(
  options: MenuOption[],
  selections: Selections,
): { group: string; values: string[] }[] {
  return visibleOptions(options, selections)
    .map((o) => ({ group: o.name, values: selectedLabels(selections[o.slug]) }))
    .filter((x) => x.values.length > 0);
}

/**
 * Peut-on ajouter au panier ? Sur les groupes VISIBLES :
 *  - obligatoire → au moins une valeur (et >= min s'il est défini),
 *  - max (s'il est défini et > 0) → pas dépassé.
 * Les groupes masqués (sous-options non déclenchées) ne bloquent pas.
 */
export function canAddToCart(options: MenuOption[], selections: Selections): boolean {
  return visibleOptions(options, selections).every((o) => {
    const labels = selectedLabels(selections[o.slug]);
    if (o.required && labels.length === 0) return false;
    if (o.required && o.min > 0 && labels.length < o.min) return false;
    if (o.max != null && o.max > 0 && labels.length > o.max) return false;
    return true;
  });
}
